import { Nullable } from '@zakeke/zakeke-3d-core';
import _ from 'lodash';
import { Exception } from './exceptions';
import { PlatformAttributeSelection } from './integrations/Integration';

export enum Platform {
	Magento1 = 5,
	Magento2 = 1,
	Shopify = 3,
	WooCommerce = 2
}

export enum PlatformPriceType {
	Standard,
	Inline
}

export class User {
	constructor(public id: number, public username: string, public name: string) {}
}

enum ZakekeEnvironment {
	Customizer = 1,
	Composer = 2,
	Viewer = 3
}

export enum TryOnType {
	NotSelected = 0,
	Eyewear = 1,
	Scarves = 2,
	Earrings = 3,
	Headwear = 4,
	Necklace = 5,
	Shoes = 6,
	Watches = 7,
	Bracelet = 8
}
export type TryOnProvidersTypesMapping = {
	list: Record<TryOnType, TryOnProvider> | {};
};
export interface TryOnProduct {
	productID: number;
	typeID: number;
	providerID: number;
	settings: string;
}
export interface TryOnSeller {
	sellerID: number;
	typeID: number;
	providerID: number;
	settings: string;
	vtoState: SellerTryOnState;
}
export enum SellerTryOnState {
	NotActive = 0,
	Active = 1
}
export enum TryOnProvider {
	NotSelected = 0,
	Mazing = 1,
	Banuba = 2,
	DeepAR = 3
}

export interface TryOnTypeSettings {
	try_on: boolean;
	pd?: boolean;
	active: boolean;
}

export enum TryOnSettingsType {
	VirtualTryOn = 'try_on',
	GlassPupillaryDistanceTool = 'pd',
	StyleButton = 'styleButton'
}
export interface TryOnButtonCustomization {
	image: Nullable<string>;
	textLabel: Nullable<string>;
	textColor: Nullable<string>;
	backgroundColor: Nullable<string>;
	borderColor: Nullable<string>;
	borderWidth: number;
	borderRadius: number;
}
export interface TryOnSettings {
	type: TryOnType;
	styleButton: 'single' | 'multiple';
	buttonCustomizations: TryOnButtonCustomization[];
	typeSettings: TryOnTypeSettings;
}
export enum TryOnFineTuningMazingEnvironment {
	Neutral = 'neutral.hdr',
	Light = 'light.hdr',
	Bright = 'bright.hdr',
	Studio = 'studio.hdr',
	Court = 'court.hdr',
	Blurred_Studio = 'startup.hdr'
}
export interface TryOnFineTuningSettings {
	scale: number;
	position: {
		x: number;
		y: number;
		z: number;
	};
	rotation?: {
		x: number;
		y: number;
		z: number;
	};
	environment?: string;
}
export interface TryOnFineTuningMazingSettings extends TryOnFineTuningSettings {
	temple: boolean;
	environment: TryOnFineTuningMazingEnvironment;
}

export interface TryOnFineTuningBanubaSettings extends TryOnFineTuningSettings {
	rotation: {
		x: number;
		y: number;
		z: number;
	};
}

export interface TryOnFineTuningDeepARSettings extends TryOnFineTuningSettings {
	position: {
		x: number;
		y: number;
		z: number;
	};
	rotation: {
		x: number;
		y: number;
		z: number;
	};
	scale: number;
	environment: string;
}

export type TryOnGenericFineTuningSettings = TryOnFineTuningSettings | TryOnFineTuningMazingSettings;

export const DefaultMazingFineTuningSettings: TryOnFineTuningMazingSettings = {
	temple: false,
	environment: TryOnFineTuningMazingEnvironment.Neutral,
	position: {
		x: 0,
		y: 0,
		z: 0
	},
	scale: 0
};

export const DefaultBanubaFineTuningSettings: TryOnFineTuningBanubaSettings = {
	rotation: {
		x: 0,
		y: 0,
		z: 0
	},
	position: {
		x: 0,
		y: 0,
		z: 0
	},
	scale: 1
};

export const DefaultDeepARFineTuningSettings: TryOnFineTuningDeepARSettings = {
	rotation: {
		x: 0,
		y: 0,
		z: 0
	},
	position: {
		x: 0,
		y: 0,
		z: 0
	},
	scale: 1,
	environment: 'https://filescdn.zakeke.com/cdn/3d/assets/environments/deepAR_HDRs/Warehouse-with-lights_(intermediate)_new_512.hdr'
};

export class Product {
	public id!: number;
	public sku!: string;
	public name!: string;
	public previewUrl!: string;
	public metadata?: any;
	public areas: ProductArea[] = [];
	public quantityRule: ProductQuantityRule | null = null;
	public quoteRule: QuoteRule | null = null;
	public environment: ZakekeEnvironment | null = null;
	public isShowSecondScreenEnabled: boolean = false;
	public isAiConfigurationEnabled: boolean = false;
	public isOldViewer: boolean = false;

	public static fromProduct(zkProduct: any) {
		const product = new Product();

		product.id = zkProduct.id;
		product.sku = zkProduct.get('code');
		product.name = zkProduct.get('name');
		product.previewUrl = zkProduct.get('largeImageUrl');
		product.quantityRule = zkProduct.get('quantityRule') ? ProductQuantityRule.fromQuantityRule(zkProduct.get('quantityRule')) : null;
		product.areas =
			zkProduct.get('colors').length > 0
				? zkProduct
						.get('colors')
						.at(0)
						.get('sides')
						.map((side) => ProductArea.fromSide(side))
				: [];
		product.quoteRule = zkProduct.get('quoteRule') ? new QuoteRule(zkProduct.get('quoteRule')) : null;
		product.environment = zkProduct.get('environment');
		product.isShowSecondScreenEnabled = zkProduct.get('isShowSecondScreenEnabled');
		product.isAiConfigurationEnabled = zkProduct.get('isAiConfigurationEnabled');
		product.isOldViewer = zkProduct.get('isOldViewer');

		return product;
	}
}

export class QuoteRule {
	public quoteRuleID: number;
	public name: string;
	public sellerID: number;
	public formControlsData: string;
	public isValidForAllProducts: boolean;
	public allowAddToCart: boolean;
	public products: number[];

	constructor(quoteRule: any) {
		this.quoteRuleID = quoteRule.quoteRuleID;
		this.name = quoteRule.name;
		this.sellerID = quoteRule.sellerID;
		this.formControlsData = quoteRule.formControlsData;
		this.isValidForAllProducts = quoteRule.isValidForAllProducts;
		this.allowAddToCart = quoteRule.allowAddToCart;
		this.products = quoteRule.products;
	}
}

export class Quote {
	public quoteID: number | undefined;
	public sellerID: number | undefined;
	public subject: string | undefined;
	public statusID: number = 1;
	public designDocID: string | undefined;
	public compositionDocID: string | undefined | null;
	public orderDocID: string | undefined;
	public formData: string | undefined;
	public amount: number | undefined;
	public quantity: number = 1;
	public shippingAmount: number | undefined;
	public additionalAmountLabel: string | undefined;
	public additionalAmount: number | undefined;
	public visitorID: number | undefined;
	public visitorCode: string | undefined;
	public customerID: number | undefined;
	public customerCode: string | undefined;
	public createdOn: Date | undefined;
	public modelPrice: number = 0;
	public variantName: string | undefined;
}
export class ProductArea {
	public id!: number;
	public name!: string;
	public cameraLocationID?: string;

	static fromSide(modelSide: any) {
		let area = new ProductArea();

		area.id = modelSide.id;
		area.name = modelSide.get('name');
		area.cameraLocationID = modelSide.get('cameraLocationID');

		return area;
	}
}

export class ProductQuantityRule {
	public minQuantity: number | null = null;
	public maxQuantity: number | null = null;
	public step: number | null = null;

	static fromQuantityRule(quantityRule: any) {
		if (!quantityRule) throw 'Invalid quantity rule';

		let rule = new ProductQuantityRule();

		rule.minQuantity = quantityRule.get('minQuantity');
		rule.maxQuantity = quantityRule.get('maxQuantity');
		rule.step = quantityRule.get('step');

		return rule;
	}
}

export class Variant {
	constructor(
		public id: number | undefined,
		public sku: string | undefined,
		public name: string | undefined,
		public price: number | undefined,
		public metadata?: any | undefined
	) {}
}

export class ZakekeEvent {
	constructor(public event: string, public callback: any) {}
}

export class TemplateConstraints {
	public isUserImageAllowed: boolean = true;
}

export class TemplateArea {
	public id: number = -1;
	public uploadRestrictions: TemplateConstraints = new TemplateConstraints();
	public disableSellerImages: boolean = false;
	public canAddImage: boolean = true;
	public canAddText: boolean = true;

	static fromTemplateArea(zkSide: any) {
		let side = new TemplateArea();

		side.id = zkSide.get('sideID');
		side.disableSellerImages = zkSide.get('disableSellerImages');
		side.canAddImage = zkSide.get('canAddImage');
		side.canAddText = zkSide.get('canAddText');

		if (zkSide.get('uploadRestrictions'))
			side.uploadRestrictions = _.mergeWith(new TemplateConstraints(), zkSide.get('uploadRestrictions'), (a, b) => (b != undefined ? b : a));

		return side;
	}
}

export class Template {
	public id: number = -1;
	public name: string = '';
	public areas: TemplateArea[] = [];

	static fromTemplate(zkTemplate: any) {
		let template = new Template();
		template.id = zkTemplate.get('templateDesignID');
		template.name = zkTemplate.get('name');
		template.areas = zkTemplate.get('sides').map((x) => TemplateArea.fromTemplateArea(x));
		return template;
	}
}

export enum ItemType {
	Text,
	Image
}

export interface Item {
	type: ItemType;
	guid: string;
	name: string;
	areaId: number;
	index: number;
	constraints: { [key: string]: any } | null;
}

export class TextItem implements Item {
	public type: ItemType;
	public areaId!: number;
	public guid!: string;
	public name!: string;
	public text!: string;
	public strokeColor!: string;
	public strokeWidth!: number;
	public fillColor!: string;
	public fontFamily!: string;
	public fontSize!: number;
	public fontWeight!: string;
	public fontStyle!: string;
	public fontStretch!: string;
	public justification!: string;
	public isTextOnPath!: boolean;
	public isTemplateElement!: boolean;
	public constraints: { [key: string]: any } | null = null;
	public index: number = 0;

	constructor(item: any) {
		this.type = ItemType.Text;
		this.areaId = item.sideID;
		this.guid = item.itemGuid;
		this.name = item.designItem.get('name');
		this.text = item.text;
		this.strokeColor = item.strokeColor;
		this.strokeWidth = item.strokeWidth;
		this.fillColor = item.fillColor;
		this.fontFamily = item.fontFamily;
		this.fontSize = item.fontSize;
		this.fontWeight = item.fontWeight;
		this.fontStyle = item.fontStyle;
		this.fontStretch = item.fontStretch;
		this.justification = item.justification;
		this.isTextOnPath = item.isTextOnPath;
		this.isTemplateElement = item.item.data.isTemplateElement ?? false;
		this.constraints = item.constraints;
		this.index = item.designItem.get('index');
	}
}

export class ImageItem implements Item {
	public type: ItemType;
	public imageID: number;
	public areaId!: number;
	public guid!: string;
	public name!: string;
	public url!: string;
	public deleted!: boolean;
	public constraints: { [key: string]: any } | null = null;
	public index: number = 0;

	constructor(item: any) {
		this.type = ItemType.Image;
		this.imageID = item.imageID;
		this.areaId = item.sideID;
		this.guid = item.itemGuid;
		this.name = item.designItem.get('name');
		this.url = item.url;
		this.deleted = !item.visible;
		this.constraints = item.constraints;
		this.index = item.designItem.get('index');
	}
}

export class PlatformSettings {
	public static Empty = new PlatformSettings();

	public accessKey: string = '';
	public accessSecret: string = '';
	public adminPath: string = '';
	public apiKey: string = '';
	public apiSecret: string = '';
	public currencyCode: string = 'EUR';
	public currencyID: number | undefined = undefined;
	public definitiveUrl: string = '';
	public currencySymbol: string = '€';
	public ecommerceID: number = -1;
	public ecommerceName: number = -1;
	public hasProductsApi: boolean = false;
	public isMarketplace: boolean = false;
	public remoteUrl: string = '';
	public username: string = '';
	public password: string = '';
	public canCheckIsIntegrated: boolean = false;
	public productsApi: string = '';
	public salesChannelID: number = -1;

	get platform() {
		return this.ecommerceID as Platform;
	}

	constructor() {}

	public static fromData(response: PlatformSettingsDto) {
		if (!response) throw new Exception('Cannot create platform settings from null');

		let settings = new PlatformSettings();
		_.assign(settings, response);
		return settings;
	}
}

export interface TextSanitationResult {
	sanitizedText: string;
	dirtyChars: string[];
}

// Ajax DTO interfaces
export interface MerchantImageResultDto {
	fileNameSmall: string;
	fileNameBig: string;
	imageUrlSmall: string;
	imageUrlBig: string;
}

export interface PlatformSettingsDto {
	ecommerceID: number;
	currencyID: number | undefined;
	currencyCode: string;
	apiKey: string;
	apiSecret: string;
	accessKey: string;
	accessSecret: string;
	remoteUrl: string;
	username: string;
	password: string;
	adminPath: string;
	canCheckIsIntegrated: boolean;
	productsApi: string;
	definitiveUrl: string;
	currencySymbol: string;
	ecommerceName: number;
	hasProductsApi: boolean;
	isMarketplace: boolean;
	salesChannelID: number;
}

export interface SubscriptionInfo {
	planId: number;
	planName: string;
	startDate: string;
	showBrand: boolean;
}

export interface FontFamily {
	fontFamilyID: number;
	fontFamilyGuid: number;
	faces: FontFamilyFace[];
	name: string;
	imageUrl: string;
	cssUrl: string;
	isSystemFont: boolean;
	stretches: string;
	variations: string;
	fullLoaded?: boolean;
}

export interface FontFamilyFace {
	filesUrls: string[];
	parsedFiles: FontFamilyFaceFiles[];
	fontFaceGuid: string;
	fontFaceID: number;
	name: string;
	openTypeStretch: string;
	openTypeWeight: string;
	stretch: string;
	style: string;
	variation: string;
	version: string;
	weight: string;
}

export interface FontFamilyFaceFiles {
	url: string;
	file: any;
}
/**
 * Represents an image.
 */
export interface ZakekeImage {
	/**
	 * The ID of the image.
	 */
	imageID: number;
	/**
	 * The name of the image.
	 */
	name: string;
	/**
	 * The URL of the image's choice.
	 */
	choiceUrl: string;
	/**
	 * The preferred width of the image, if specified.
	 * Set to null if no preferred width is specified.
	 */
	preferredWidth: number | null;
	/**
	 * The preferred height of the image, if specified.
	 * Set to null if no preferred height is specified.
	 */
	preferredHeight: number | null;
}

export interface ImageCategory {
	categoryID: number | null;
	name: string;
	isSystemwide: boolean;
	hasImages: boolean;
}

export interface ImageMacroCategory {
	macroCategoryID: number | null;
	name: string;
	isSystemwide: boolean;
	hasImages: boolean;
	categories: ImageCategory[];
}

interface Addon {
	addonName: string;
	subscriptionAddonID: string;
}

export interface AdditionalFeatures {
	editPDFPreview: boolean;
	editSummary: boolean;
	integrations: boolean;
	bulkVariationForm: boolean;
	pricePerImage: boolean;
	engraving: boolean;
	editPrintfiles: boolean;
	printReadyFilesPlus: boolean;
	textplus: boolean;
	prices3dConfigurator: boolean;
	customEnvironmentTexture: boolean;
	dynamicProperties: boolean;
	materialLibrary: boolean;
	multiareaConfiguratorDesigner: boolean;
	aiPoweredTools: boolean;
}

export interface Settings {
	additionalFeatures: AdditionalFeatures;
	isPricingAdvancedEnabled: boolean;
	userID: number;
	username: string;
	referrerID: number;
	ecommerceUrl: string;
	ecommerceID: number | null;
	addons: Addon[];
	eventMessages: EventMessage[];
	nftSettings: NftSettings;
	brand: boolean;
	isPdfEpsEnabled: boolean;
}

export class EventMessage {
	public eventID: EventMessageType = -1;
	public description: string = '';
	public closeable: boolean = false;
	public visible: boolean = false;
	public addToCartDisabledIfVisible: boolean = false;
	public additionalData: any;
	public enableCheckForSellerImages: boolean = false;
	public isDefault: boolean = false;

	public static fromMessage(message: any) {
		const eventMessage = new EventMessage();
		eventMessage.eventID = message.eventID;
		eventMessage.description = message.description;
		eventMessage.closeable = message.closeable;
		eventMessage.visible = message.visible;
		eventMessage.addToCartDisabledIfVisible = message.addToCartDisabledIfVisible;
		eventMessage.additionalData = message.additionalData !== '' && message.additionalData !== null ? JSON.parse(message.additionalData) : '';
		eventMessage.enableCheckForSellerImages = message.enableCheckForSellerImages;
		eventMessage.isDefault = message.isDefault;
		return eventMessage;
	}
}

export enum EventMessageType {
	Undefined = -1,
	ImageQualityWarning = 1,
	Welcome = 3,
	AddToCart = 4,
	Copyright = 8
}

export class Camera {
	public id: string = '';
	public name: string = '';

	public static fromZkCamera(zkCamera: Zakeke.SceneCameraLocation) {
		const camera = new Camera();
		camera.id = zkCamera.get('locationID');
		camera.name = zkCamera.get('name')!;
		return camera;
	}
}

export class Group {
	public id: number = -1;
	public guid: string = '';
	public name: string = 'Group';
	public enabled: boolean = false;
	public imageUrl?: string | null = null;
	public icon?: string | null = null;
	public attributes: Attribute[] = [];
	public steps: Step[] = [];
	public templateGroups: ThemeTemplateGroup[] = [];
	public cameraLocationId: string | null = null;
	public displayOrder: number = 1;
	public direction: number = 0;
	public attributesAlwaysOpened: boolean = false;
}

export class Step {
	public id: number = -1;
	public guid: string = '';
	public name: string = 'Step';
	public cameraLocationID: string | null = null;
	public displayOrder: number = 1;
	public attributes: Attribute[] = [];
	public templateGroups: ThemeTemplateGroup[] = [];
}

export enum AttributeType {
	Select,
	Input,
	Colorpicker
}

export enum AttributeOptionShapeType {
	Round = 1,
	Square = 2
}

export class Attribute {
	public id: number = -1;
	public guid: string = '';
	public code: string = '';
	public name: string = 'Attribute';
	public description: string = '';
	public enabled: boolean = false;
	public imageUrl: string | null = null;
	public groupId: number = -1;
	public stepId: number = -1;
	public options: Option[] = [];
	public value: any = null;
	public numberFormat: string = '';
	public type: AttributeType = AttributeType.Select;
	public optionShapeType: AttributeOptionShapeType = AttributeOptionShapeType.Round;
	public displayOrder: number = 1;
	public hideOptionsLabel: boolean = false;
	public cameraLocationId: string | null = null;
}

interface Pricing {
	fromQuantity: number;
	price: number;
}

export class Option {
	public id: number = -1;
	public attribute!: Attribute;
	public guid: string = '';
	public code: string = '';
	public name: string = 'Option';
	public description: string = '';
	public enabled: boolean = false;
	public selected: boolean = false;
	public imageUrl: string | null | undefined = null;
	public displayOrder: number = 1;
	public pricings: Pricing[] = [];
}

export class TemplateComposition {
	compositionID: number = -1;
	name: string = '';
	previewImageUrl: string = '';
}
export class ThemeCategory {
	id: number = -1;
	name: string = '';
	composition: TemplateComposition[] = [];
}

export class ThemeMacroCategory {
	id: number = -1;
	name: string = '';
	category: ThemeCategory[] = [];
}

export class ThemeTemplateGroup {
	templateGroupID: number = -1;
	name: string = '';
	groupID: number = -1;
	stepID: number = -1;
	displayOrder: number = -1;
	macroCategory: ThemeMacroCategory[] = [];
}

export class SellerSettings {
	customizeButtonLabel: string = '';
	customizeButtonIconUrl: string | null = '';
	isCompositionRecapEnabled: boolean = false;
	isCompositionRecapVisibleFromStart: boolean = false;
	canUserCloseCompositionRecap: boolean = true;
	minimumFractionDigits: number = 1;
	priceInfoText: string = '';
	hidePrice: boolean = false;
	hotjarSiteID: string | null = null;
	googleTagManagerID: string | null = null;
	canSaveDraftComposition: boolean = false;
	canUndoRedo: boolean = false;
	shareType: number | null = null;
	static fromSellerSettings(zkSellerSettings: any) {
		const sellerSettings = new SellerSettings();
		sellerSettings.customizeButtonLabel = zkSellerSettings.customizeButtonLabel;
		sellerSettings.customizeButtonIconUrl = zkSellerSettings.customizeButtonIconUrl;
		sellerSettings.isCompositionRecapEnabled = zkSellerSettings.isCompositionRecapEnabled;
		sellerSettings.isCompositionRecapVisibleFromStart = zkSellerSettings.isCompositionRecapVisibleFromStart;
		sellerSettings.canUserCloseCompositionRecap = zkSellerSettings.canUserCloseCompositionRecap;
		sellerSettings.minimumFractionDigits = zkSellerSettings.minimumFractionDigits;
		sellerSettings.priceInfoText = zkSellerSettings.priceInfoText;
		sellerSettings.hidePrice = zkSellerSettings.hidePrice;
		sellerSettings.hotjarSiteID = zkSellerSettings.hotjarSiteID;
		sellerSettings.googleTagManagerID = zkSellerSettings.googleTagManagerID;
		sellerSettings.canSaveDraftComposition = zkSellerSettings.canSaveDraftComposition;
		sellerSettings.canUndoRedo = zkSellerSettings.canUndoRedo;
		sellerSettings.shareType = zkSellerSettings.shareType;

		return sellerSettings;
	}
}

export class CompositionItem {
	public attributeID: number = -1;
	public attributeName: string = '';
	public attributeCode: string = '';
	public selectedOptionName: string = '';
	public selectedOptionCode: string = '';
}

export class Composition {
	public name: string = '';
	public docID: string = '';
	public compositionID: number = -1;
	public compositionItems: CompositionItem[] = [];
	public previewImageUrl: string = '';
	public designID: number | null = null;
	public pdSettings: number | null = null;
	public tags: string[] = [];

	static fromJSON(json: any) {
		let composition = new Composition();
		_.assign(composition, json);
		return composition;
	}
}

export interface OnBeforeSendDataToParent {
	composition: number;
	preview: string;
	attributes: PlatformAttributeSelection[];
	designItems: Item[];
	quantity: number;
	previewFiles: PreviewFile[];
	additionalProperties: any;
}

export interface TemplateUploadRestrictions {
	isUserImageAllowed: boolean;
	isJpgAllowed: boolean;
	isPngAllowed: boolean;
	isSvgAllowed: boolean;
	isPdfAllowed: boolean;
	isEpsAllowed: boolean;
	isPdfWithRasterAllowed: boolean;
}

export interface PrintingMethodsRestrictions {
	allowedBold: boolean;
	allowedItalic: boolean;
	allowedCurved: boolean;
	disableTextColors: boolean;
	textColors: Array<RestrictionColor>;
	defaultColor: string;
	isPDFPreviewEnabled: boolean;
}

export declare class SelectedOption {
	attributeID: number;
	optionID: number;
}

export enum ExtensionFieldDataType {
	Money = 1,
	Integer = 2,
	Decimal = 3
}

export interface ExtensionFields {
	name: string;
	value: number;
	label: string;
	formatString: string;
}

export interface PreviewCameraImage {
	id: number;
	url: string;
}

export interface CompositionClientPreview {
	fileObjectID: number;
	cameraName: string;
	url: string;
}

export interface NftSettings {
	isNFTEnabled: boolean;
	priceToAdd: number;
}

export interface NftForm {
	email: string;
	walletAddress: string;
}

export interface SelectedCategory {
	categoryID: number;
	name: string;
}
export interface VisibleEventMessage {
	areaID: number;
	itemGuid: string;
	type: number;
	title: string;
	message: string;
	closabled: boolean;
	requiredWidth: number | null;
	requiredHeight: number | null;
	addToCartDisabledIfVisible: boolean;
}

export interface PreviewFile {
	url: string;
	cameraName: string;
}
/**
 * Represents internal translations.
 */
export interface InternalTranslations {
	/**
	 * An array of static translations.
	 */
	statics: StaticTranslation[];

	/**
	 * An array of dynamic translations.
	 */
	dynamics: DynamicTranslation[];
}

/**
 * Represents translations.
 */
export interface Translations {
	/**
	 * A map of static translations where the original string is the key and the localized string is the value.
	 */
	statics: Map<string, string>;

	/**
	 * A map of dynamic translations where the original string is the key and the localized string is the value.
	 */
	dynamics: Map<string, string>;
}

/**
 * Represents a static translation.
 */
interface StaticTranslation {
	/**
	 * The original string to be translated.
	 */
	original: string;

	/**
	 * The localized string representing the translation.
	 */
	localization: string;

	/**
	 * The language identifier (LCID) associated with the translation.
	 */
	lcid: number;
}

/**
 * Represents a dynamic translation.
 */
interface DynamicTranslation {
	/**
	 * The original string to be translated.
	 */
	original: string;

	/**
	 * The localized string representing the translation.
	 */
	localization: string;

	/**
	 * The language identifier (LCID) associated with the translation.
	 */
	lcid: number;
}

/**
 * Represents a color restriction.
 */
export interface RestrictionColor {
	/**
	 * The color code.
	 */
	colorCode: string;
	/**
	 * Indicates if it is the default color.
	 */
	isDefault: boolean;
}

/**
 * Represents custom preview size.
 */
export interface CustomPreviewSize {
	/**
	 * The width of the custom cart preview.
	 */
	customCartPreviewWidth: number;
	/**
	 * The height of the custom cart preview.
	 */
	customCartPreviewHeight: number;
}

/**
 * Represents draft compositions.
 */
export interface DraftCompositions {
	/**
	 * The name of the composition.
	 */
	name: string;
	/**
	 * The document ID of the composition.
	 */
	docID: string;
	/**
	 * The URL of the preview image.
	 */
	previewImageUrl: string;
	/**
	 * The tags associated with the composition.
	 */
	tags: string[];
}

/**
 @internal
 */
export interface ThemeCompositions {
	/**
	 * The name of the composition.
	 */
	name: string;
	/**
	 * The document ID of the composition.
	 */
	docID: string;
	/**
	 * The URL of the preview image.
	 */
	previewImageUrl: string;
	/**
	 * The tags associated with the composition.
	 */
	tags: string[];
}

/**
 * Represents information about the current composition.
 */
export interface CurrentCompositionInfo {
	/**
	 * The ID of the composition. It can be null.
	 */
	compositionId: string | null;
	/**
	 * The name of the composition. It can be null.
	 */
	compositionName: string | null;
	/**
	 * The tags associated with the composition. It can be null.
	 */
	compositionTags: string[] | null;
}

/**
 * Represents a template category.
 */
export interface TemplateCategory {
	/**
	 * The name of the category.
	 */
	categoryName: string;
	/**
	 * The ID of the category.
	 */
	categoryID: number;
}

/**
 * Represents a template macro category.
 */
export interface TemplateMacroCategory {
	/**
	 * The name of the macro category.
	 */
	macroCategoryName: string;
	/**
	 * The ID of the macro category.
	 */
	macroCategoryID: number;
	/**
	 * The categories under this macro category.
	 */
	categories: TemplateCategory[];
}

/**
 @internal
 */
export interface Category {
	/**
	 * The ID of the category.
	 */
	categoryID: number;
	/**
	 * The ID of the macro category to which this category belongs.
	 */
	macroCategoryID: number;
	/**
	 * The name of the category.
	 */
	name: string;
	/**
	 * The description of the category.
	 */
	description: string;
	/**
	 * The ID of the seller associated with the category.
	 */
	sellerID: number;
	/**
	 * The display order of the category.
	 */
	displayOrder: number;
	/**
	 * Indicates if the category is a draft.
	 */
	isDraft: boolean;
	/**
	 * The composition of the category.
	 */
	composition: any;
	/**
	 * The template of the category.
	 */
	template: any;
}

/**
 @internal
 */
export interface MacroCategory {
	/**
	 * The ID of the macro category.
	 */
	macroCategoryID: number;
	/**
	 * The name of the macro category.
	 */
	name: string;
	/**
	 * The ID of the seller associated with the macro category.
	 */
	sellerID: number;
	/**
	 * The display order of the macro category.
	 */
	displayOrder: number;
	/**
	 * Indicates if the macro category is a draft.
	 */
	isDraft: boolean;
	/**
	 * The categories under this macro category.
	 */
	category: Category[];
}

/**
 * Represents the boot parameters for the Zakeke Configurator.
 */
export interface BootParameters {
	/**
	 * The name of the product.
	 */
	productName?: string;
	/**
	 * The platform on which the Composer is running.
	 */
	platform?: string;
	/**
	 * The quantity of the product.
	 */
	quantity?: number;
	/**
	 * The culture of the product.
	 */
	culture?: string;
	/**
	 * The currency of the product.
	 */
	currency?: string;
	/**
	 * The token for the product.
	 */
	token?: string;
	/**
	 * The ID of the composition.
	 */
	compositionId?: string;
	/**
	 * The code of the product.
	 */
	productCode?: string;
	/**
	 * The width of the preview.
	 */
	previewWidth?: number;
	/**
	 * The height of the preview.
	 */
	previewHeight?: number;
	/**
	 * The prefix for the share URL.
	 */
	shareUrlPrefix?: string;
	/**
	 * The ID of the shared composition document.
	 */
	sharedCompositionDocId?: string;
	/**
	 * The ID of the template.
	 */
	templateId?: string;
	/**
	 * Indicates if the editor is in draft mode.
	 */
	isDraftEditor?: boolean;
	/**
	 * Indicates if the share composition URL is enabled.
	 */
	enableShareCompositionUrl?: boolean;
	/**
	 * The ID of the draft composition document.
	 */
	draftCompositionDocId?: any;
	/**
	 * Indicates if the editor is in viewer mode.
	 */
	isViewerMode?: boolean;
	/**
	 * Indicates if the editor is in template mode.
	 */
	isTemplateEditor?: boolean;
	/**
	 * Indicates if the legacy screenshot is used.
	 */
	useLegacyScreenshot?: boolean;
	/**
	 * The version of the integration.
	 */
	integrationVersion?: number;
}

export interface UIConfig {
	urlLoader: string;
	urlARIcon: string;
}

export interface SceneAnimationsConfig {
	runAtStart?: boolean | null;
	loop?: boolean | null;
	speed?: number | null;
}

export interface DAMViewerAnalyticsType {
	modelCode: string;
	userId: number;
	eventType: DAMViewerAnalyticsEventType;
}

export enum DAMViewerAnalyticsEventType {
	ViewProduct = 'VIEW_PRODUCT',
	ViewArProdutc = 'VIEW_AR_PRODUCT'
}

export interface GraphQLWelcomeMessageResponse {
	data: {
		messagesByProduct: null | GraphQLWelcomeMessageData[];
	};
}
export interface GraphQLWelcomeMessageData {
	id: string;
	eventGlobalID: string;
	messageId: number;
	messageText: string;
	eventName: string;
	eventId: number;
	closeable: boolean;
	visible: boolean;
	addToCartDisabledValue: boolean;
	enableCheckForSellerImagesValue: boolean;
	enableAdditionalMessage: boolean;
	additionalMessage: string | null;
	eventMessageId: number;
	title: string | null;
	titleSingleMessage: string | null;
	infoLink: string | null;
	defaultMessage: string | null;
	visibleOnlyOnApi: boolean | null;
	addToCartLabel: string | null;
	visibleLowQualityInfoText: boolean | null;
	defaultAdditionalMessageText: string | null;
	warningText: string | null;
	onDeleteWarningText: string | null;
	isDefault?: boolean | null; // Optional property
}

/**
 * AI Studio Types
 * These interfaces are used for AI-powered product staging and virtual try-on features
 */

/**
 * Represents a category for AI presets
 */
export interface AIPresetCategory {
	id: number;
	name: string;
	toolId: string;
	presetType: string;
	sellerID: number | null;
	isActive: boolean;
	createdDate: string;
	modifiedDate: string;
}

/**
 * Represents an AI preset configuration
 */
export interface AIPreset {
	id: number;
	name: string;
	toolId: string;
	presetType: string;
	categoryId: number | null;
	prompt: string | null;
	negativePrompt: string | null;
	imageUrl: string | null;
	previewUrl: string | null;
	factor: number | null;
	description: string | null;
	sellerID: number | null;
	isActive: boolean;
	sortOrder: number;
	createdDate: string;
	modifiedDate: string;
	categoryName: string | null;
}

/**
 * Represents rules for AI product staging
 */
export interface AIProductStagingRule {
	ruleID: number;
	name: string;
	sellerID: number;
	allowCustomBackground: boolean;
	numberOfImages: number;
	createdDate: string;
	productIDs: number[];
	printTypeIDs: number[];
	backgroundCategoryIDs: number[];
	surfacePresetIDs: number[];
	moodPresetIDs: number[];
	effectPresetIDs: number[];
}

/**
 * Options for AI product staging generation
 */
export interface AIProductStagingOptions {
	productImageUrl: string;
	backgroundType?: string;
	backgroundPreset?: number;
	backgroundImageUrl?: string;
	surfacePreset?: number;
	moodPreset?: number;
	customPrompt?: string;
	effectPreset?: number;
	progress?: (progress: number) => void;
}

/**
 * Result from AI product staging generation
 */
export interface AIProductStagingResult {
	imageUrl: string;
	taskId: string;
}

/**
 * Options for AI virtual try-on generation
 */
export interface AIVirtualTryOnOptions {
	personImageUrl?: string;
	personPreset?: number;
	clothingImageUrl?: string;
	clothingPreset?: number;
	backgroundImageUrl?: string;
	backgroundPreset?: number;
	customPrompt?: string;
	effectPreset?: number;
	posePreset?: number;
	progress?: (progress: number) => void;
}

/**
 * Result from AI virtual try-on generation
 */
export interface AIVirtualTryOnResult {
	imageUrl: string;
	taskId: string;
}

/**
 * Service availability flags from subscription API
 */
export interface AIServicesAvailability {
	GENERATIVE_AI_DESIGN: boolean;
	GENERATIVE_AI_IMAGE_GENERATOR: boolean;
	GENERATIVE_AI_PRODUCT_STAGING: boolean;
	GENERATIVE_AI_VTO: boolean;
	VECTORIZE: boolean;
}

/**
 * VTO filtering rules (from /api/v1/AIVirtualModelRule/applicable)
 */
export interface AIVirtualModelRule {
	ruleID: number;
	name: string;
	sellerID: number;
	numberOfImages: number;
	allowCustomBackground: boolean;
	createdDate: string;
	productIDs: number[];
	printTypeIDs: number[];
	backgroundCategoryIDs: number[];
	effectPresetIDs: number[];
	modelPresetIDs: number[];
	poseCategoryIDs: number[];
}

// #region AI Rate Limiting

/**
 * Tool types for AI rate limiting
 */
export type AIToolType =
	| 'AIStudio'
	| 'CustomizerAIImageGenerator'
	| 'CustomizerProductStaging'
	| 'CustomizerVirtualTryOn'
	| 'CustomizerDesignGenerator';

/**
 * Rule level for tool usage
 */
export type AIRuleLevel = 'Seller' | 'Global';

/**
 * Limit period type for tool usage
 */
export type AILimitPeriodType = 'Session' | 'Minutes';

/**
 * Tool usage rule for AI rate limiting
 */
export interface AIToolUsageRule {
	toolType: AIToolType;
	hasRule: boolean;
	toolUsageRuleID: number | null;
	ruleLevel: AIRuleLevel | null;
	allowLoggedUsers: boolean | null;
	allowNonLoggedUsers: boolean | null;
	limitEnabled: boolean | null;
	limitPeriodType: AILimitPeriodType | null;
	maxOperations: number | null;
	limitPeriodMinutes: number | null;
}

/**
 * Error thrown when AI generation rate limit is reached.
 * Contains the tool usage rule and last generation timestamp for calculating remaining cooldown.
 */
export class AIRateLimitError extends Error {
	public readonly toolUsageRule: AIToolUsageRule;
	public readonly lastGenerationTime?: number;

	constructor(message: string, toolUsageRule: AIToolUsageRule, lastGenerationTime?: number) {
		super(message);
		this.name = 'AIRateLimitError';
		this.toolUsageRule = toolUsageRule;
		this.lastGenerationTime = lastGenerationTime;
	}
}

// #endregion
